<template>
    <BaseMapShell v-model:panelOpen="panelOpen" v-model:rightPanelOpen="rightPanelOpen"
        v-model:activeRightPanelId="activeRightPanelId" :center="center" :zoom="zoom" :bounds="bounds"
        :max-bounds="maxBounds" :maxZoom="19" :minZoom="3" :right-panels="rightPanels" @leaflet-click="addPointFromMap">
        <!-- Slot panel-sections : rien pour l’instant -->
        <template #panel-sections />

        <!-- Calques spécifiques geofencing -->
        <template>
            <GeofencingLayer :polygon="selectedPolygon" :draft-points="draftPoints" :is-drawing="isDrawing" />
        </template>

        <!-- Panneau droit : liste & actions (panel 'geofencing') -->
        <template #right-panel="{ activePanel }">
            <RightGeofencingPanel v-if="activePanel === 'geofencing'" :client-id="effectiveClientId"
                :geofencings="geofencings" :selected-id="selectedId" :loading="loadingList || loadingGeom"
                :is-drawing="isDrawing" :draft-count="draftPoints.length" @refresh="fetchList" @select="handleSelect"
                @start-create="startCreate" @start-edit="startEdit" @cancel-edit="cancelEdit" @save-create="saveCreate"
                @save-edit="saveEdit" @delete="handleDelete" />
        </template>
    </BaseMapShell>
</template>

<script>
import "leaflet/dist/leaflet.css";
import GeofencingLayer from "../Components/layers/GeofencingLayer.vue";
import BaseMapShell from "../BaseMapShell.vue";
import UserHelper from "@/helpers/user.helper.js";
import RightGeofencingPanel from "../Components/rightPanel/RightGeofencingPanel.vue";
import GeofencingService from "@/services/geofencing.service.js";
import { computePaddedBounds } from "../composables/useBounds";

export default {
    name: "GeofencingMap",
    components: {
        BaseMapShell,
        GeofencingLayer,
        RightGeofencingPanel,
    },
    props: {
        clientId: { type: Number, required: false },
    },
    data() {
        return {
            panelOpen: false,
            rightPanelOpen: true, // Ouvert par défaut
            activeRightPanelId: "geofencing",
            rightPanels: [
                {
                    id: "geofencing",
                    icon: "☰ Geofencing", // l’icône que tu veux (emoji, etc.)
                    title: "Geofencings",
                },
            ],
            zoom: 12,
            center: [48.86, 2.35],
            bounds: [],
            maxBounds: [],
            geofencings: [],
            selectedId: null,
            selectedPolygon: [],
            loadingList: false,
            loadingGeom: false,
            // édition
            isDrawing: false,
            creating: false,
            draftPoints: [],
            draftName: "",
            searchTerm: "",
        };
    },
    computed: {
        effectiveClientId() {
            // 1) prop > 2) user.clientId > 3) clientIdToWatch (admin/op)
            const fromStore = this.$store?.state?.auth?.user?.clientId ?? null;
            const fromWatcher = UserHelper?.getClientId?.() ?? null; // peut être 0 = "Tous les clients"
            return this.clientId ?? fromStore ?? (fromWatcher && Number(fromWatcher) > 0 ? Number(fromWatcher) : null);
        },
    },
    watch: {
        selectedPolygon(newPoly) {
            if (newPoly.length >= 1) {
                // newPoly = [[lat, lng], ...] -> on adapte au format attendu par computePaddedBounds
                const positions = newPoly.map((p) => ({
                    lat: p.latitude,
                    lng: p.longitude,
                }));
                this.bounds = computePaddedBounds(positions, this.rightPanelOpen);
                this.center = [
                    newPoly[0].latitude,
                    newPoly[0].longitude,
                ];
            } else {
                // Aucun polygone sélectionné -> on nettoie les bounds
                this.bounds = [];
            }
        },
    },
    mounted() {
        this.fetchList();
    },
    methods: {
        async fetchList(term) {
            if (!this.effectiveClientId) return;
            this.loadingList = true;
            try {
                if (typeof term === "string") {
                    this.searchTerm = term.trim();
                }
                const { data } = await GeofencingService.getClientGeofencings(
                    this.effectiveClientId,
                    this.searchTerm || ""
                );
                this.geofencings = data || [];

                // Vérifier si le geofencing sélectionné est toujours dans les résultats
                const stillExists = this.geofencings.some(g => g.id === this.selectedId);

                if (!stillExists) {
                    if (this.geofencings.length) {
                        // On sélectionne le premier des résultats filtrés
                        await this.handleSelect(this.geofencings[0].id);
                    } else {
                        // Plus aucun résultat -> on nettoie la sélection & la carte
                        this.selectedId = null;
                        this.selectedPolygon = [];
                        this.bounds = [];
                    }
                }
            } finally {
                this.loadingList = false;
            }
        },
        async handleSelect(id) {
            this.selectedId = id;
            this.isDrawing = false;
            this.creating = false;
            this.draftPoints = [];
            this.selectedPolygon = [];
            if (!id) return;
            this.loadingGeom = true;
            try {
                const { data: pts } = await GeofencingService.getGeofencingPoints(id);
                this.selectedPolygon = (pts || [])
                    .sort((a, b) => a.orderIndex - b.orderIndex)
                    .map((p) => ({
                        latitude: Number(p.latitude),
                        longitude: Number(p.longitude),
                    }));
            } finally {
                this.loadingGeom = false;
            }
        },
        // --- Création ---
        startCreate() {
            this.selectedId = null;
            this.creating = true;
            this.isDrawing = true;
            this.draftPoints = [];
            this.draftName = "";
        },
        async saveCreate(nameArg) {
            if (!this.effectiveClientId || Number(this.effectiveClientId) === 0) {
                alert("Veuillez d’abord sélectionner un client (menu en haut à droite).");
                return;
            }
            if (!this.creating || !this.isDrawing) return;
            if (this.draftPoints.length < 3) return;
            const name = (nameArg ?? this.draftName ?? "").trim();
            if (!name) return;
            await GeofencingService.create(this.effectiveClientId, name, this.pointsPayload(this.draftPoints));
            await this.fetchList();
            // sélection du dernier créé
            const last = this.geofencings[this.geofencings.length - 1];
            await this.handleSelect(last?.id || null);
            this.isDrawing = false;
            this.creating = false;
            this.draftPoints = [];
            this.draftName = "";
        },
        // --- Edition complète du polygone ---
        startEdit() {
            if (!this.selectedId) return;
            this.isDrawing = true;
            this.creating = false;
            this.draftPoints = [];
        },
        async saveEdit() {
            if (!this.selectedId || !this.isDrawing) return;
            if (this.draftPoints.length < 3) return;
            await GeofencingService.replacePoints(this.selectedId, this.pointsPayload(this.draftPoints));
            await this.handleSelect(this.selectedId);
            this.isDrawing = false;
            this.draftPoints = [];
        },
        cancelEdit() {
            this.isDrawing = false;
            this.creating = false;
            this.draftPoints = [];
        },
        // --- Suppression ---
        async handleDelete(id) {
            if (!id) return;
            await GeofencingService.delete(id);
            if (this.selectedId === id) {
                this.selectedId = null;
                this.selectedPolygon = [];
            }
            await this.fetchList();
        },
        // --- Map interactions ---
        // On capte les clics au niveau global (via événement Leaflet sur map)
        addPointFromMap(latlng) {
            if (!this.isDrawing) return;
            this.draftPoints = [
                ...this.draftPoints,
                {
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                }
            ];
        },
        pointsPayload(points) {
            return points.map((p) => ({
                latitude: p.latitude,
                longitude: p.longitude,
            }));
        }
    },
    // hook Leaflet (via provide/inject) : on écoute le clic sur la map
    provide() {
        // Titre spécifique pour le panneau droit de cette carte
        return {
            rightPanelTitle: 'Geofencings',
        };
    },
};
</script>

<style scoped>
/* rien de spécifique, on garde l’esthétique du shell */
</style>
